import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getChargerDetailed,
  getChargerPhotos,
  getSites,
  updateCharger,
  uploadChargerPhoto,
  deleteChargerPhoto,
  setChargerPhotoCover
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Save, MapPin, Upload, Image, Trash2, Star, StarOff,
  Loader2, AlertTriangle, Zap, Info, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '300px'
};

const defaultCenter = { lat: 41.3275, lng: 19.8187 }; // Tirana
const libraries = ['places'];

const ChargerEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const [charger, setCharger] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    is_public: true,
    site_id: '',
    site_area: '',
    max_power_kw: '',
    price_per_kwh: '',
    address: '',
    city: '',
    country: '',
    street: '',
    latitude: null,
    longitude: null,
    amenities: []
  });
  
  // Available amenities list
  const AMENITIES_LIST = [
    { id: 'parking', name: 'Parkim', icon: '🅿️' },
    { id: 'wifi', name: 'Wi-Fi', icon: '📶' },
    { id: 'cafe', name: 'Kafe', icon: '☕' },
    { id: 'restaurant', name: 'Restorant', icon: '🍽️' },
    { id: 'wc', name: 'WC', icon: '🚻' },
    { id: 'market', name: 'Market', icon: '🏪' },
    { id: '24h', name: '24/7', icon: '🕐' },
    { id: 'lighting', name: 'Ndriçim', icon: '💡' },
    { id: 'security_camera', name: 'Kamera sigurie', icon: '📹' },
    { id: 'accessible', name: 'Akses për invalidë', icon: '♿' },
    { id: 'shelter', name: 'Hije / Strehë', icon: '⛱️' },
  ];
  
  // Map state - Google Maps uses {lat, lng} object
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  
  // Photo preview
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Reverse geocode using Google Maps Geocoding API
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}&language=sq`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results?.length > 0) {
        const result = data.results[0];
        const components = result.address_components || [];
        
        let city = '', country = '', street = '';
        components.forEach(comp => {
          if (comp.types.includes('locality')) city = comp.long_name;
          if (comp.types.includes('administrative_area_level_1') && !city) city = comp.long_name;
          if (comp.types.includes('country')) country = comp.long_name;
          if (comp.types.includes('route')) street = comp.long_name;
        });
        
        setFormData(prev => ({
          ...prev,
          address: result.formatted_address?.split(',').slice(0, 3).join(', ') || '',
          city: city || '',
          country: country || 'Shqipëri',
          street: street || '',
          latitude: lat,
          longitude: lng
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      setFormData(prev => ({
        ...prev,
        latitude: lat,
        longitude: lng
      }));
    }
  };

  // Handle map click
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setMapCenter({ lat, lng });
    reverseGeocode(lat, lng);
  };

  // Handle marker drag
  const handleMarkerDrag = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    reverseGeocode(lat, lng);
  };

  // Handle Google Places Autocomplete
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });
        
        let city = '', country = '', street = '';
        place.address_components?.forEach(comp => {
          if (comp.types.includes('locality')) city = comp.long_name;
          if (comp.types.includes('administrative_area_level_1') && !city) city = comp.long_name;
          if (comp.types.includes('country')) country = comp.long_name;
          if (comp.types.includes('route')) street = comp.long_name;
        });
        
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address?.split(',').slice(0, 3).join(', ') || '',
          city: city || '',
          country: country || 'Shqipëri',
          street: street || '',
          latitude: lat,
          longitude: lng
        }));
        
        setAddressSearch(place.name || place.formatted_address || '');
      }
    }
  };

  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [chargerRes, sitesRes, photosRes] = await Promise.all([
        getChargerDetailed(id),
        getSites(),
        getChargerPhotos(id).catch(() => ({ data: [] }))
      ]);
      
      const chargerData = chargerRes.data;
      setCharger(chargerData);
      setSites(sitesRes.data);
      setPhotos(photosRes.data || []);
      
      // Populate form
      setFormData({
        name: chargerData.name || chargerData.charger_id || '',
        is_public: chargerData.is_public !== false,
        site_id: chargerData.site_id || '',
        site_area: chargerData.site_area || '',
        max_power_kw: chargerData.max_power_kw || '',
        price_per_kwh: chargerData.price_per_kwh || '',
        address: chargerData.address || '',
        city: chargerData.city || '',
        country: chargerData.country || 'Albania',
        street: chargerData.street || '',
        latitude: chargerData.latitude || null,
        longitude: chargerData.longitude || null,
        amenities: chargerData.amenities || []
      });
      
      // Set map center if coordinates exist (Google Maps format)
      if (chargerData.latitude && chargerData.longitude) {
        setMapCenter({ lat: chargerData.latitude, lng: chargerData.longitude });
        setMarkerPosition({ lat: chargerData.latitude, lng: chargerData.longitude });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
      if (error.response?.status === 403) {
        toast.error('Nuk keni leje të modifikoni këtë karikues');
        navigate('/chargers');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Emri i karikuesit është i detyrueshëm');
      return;
    }
    
    if (formData.max_power_kw && parseFloat(formData.max_power_kw) <= 0) {
      toast.error('Fuqia duhet të jetë më e madhe se 0');
      return;
    }
    
    if (formData.price_per_kwh && parseFloat(formData.price_per_kwh) < 0) {
      toast.error('Çmimi duhet të jetë >= 0');
      return;
    }
    
    // If address is provided, require coordinates
    if (formData.address && (!formData.latitude || !formData.longitude)) {
      toast.error('Adresa kërkon koordinata - përdorni Google Maps');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {
        name: formData.name,
        is_public: formData.is_public,
        site_id: formData.site_id || undefined,
        site_area: formData.site_area || undefined,
        max_power_kw: formData.max_power_kw ? parseFloat(formData.max_power_kw) : undefined,
        price_per_kwh: formData.price_per_kwh ? parseFloat(formData.price_per_kwh) : undefined,
        amenities: formData.amenities || []
      };
      
      // Only include address if we have coordinates
      if (formData.latitude && formData.longitude) {
        dataToSend.address = formData.address;
        dataToSend.city = formData.city;
        dataToSend.country = formData.country;
        dataToSend.street = formData.street;
        dataToSend.latitude = formData.latitude;
        dataToSend.longitude = formData.longitude;
      }
      
      await updateCharger(id, dataToSend);
      toast.success('Karikuesi u përditësua me sukses');
      navigate(`/chargers/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ruajtjen e ndryshimeve');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Vetëm JPG, PNG, dhe WebP lejohen');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Foto nuk mund të jetë më e madhe se 5MB');
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadPhoto = async (isCover = false) => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('is_cover', isCover);
      
      const res = await uploadChargerPhoto(id, formData);
      toast.success('Foto u ngarkua me sukses');
      
      setPhotos(prev => [res.data.photo, ...prev]);
      setSelectedFile(null);
      setPhotoPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e fotos');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await deleteChargerPhoto(id, photoId);
      toast.success('Foto u fshi');
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error('Gabim në fshirjen e fotos');
    }
  };

  const handleSetCover = async (photoId) => {
    try {
      await setChargerPhotoCover(id, photoId);
      toast.success('Foto kryesore u vendos');
      setPhotos(prev => prev.map(p => ({
        ...p,
        is_cover: p.id === photoId
      })));
    } catch (error) {
      toast.error('Gabim');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!charger) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertTriangle className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Karikuesi nuk u gjet</p>
        <Button variant="outline" onClick={() => navigate('/chargers')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kthehu
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full" data-testid="charger-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/chargers/${id}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground font-outfit">Modifiko Karikuesin</h1>
            <p className="text-sm text-muted-foreground">{formData.name || charger.charger_id}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Ruaj
        </Button>
      </div>

      {/* Read-only info banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <Info className="w-4 h-4 text-blue-500" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-blue-500">Info:</strong> {charger.vendor} | {charger.model} | {charger.serial_number} | OCPP {charger.ocpp_version}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Row 1: Basic Info + Address & Location - 50% each */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Info */}
        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit flex items-center gap-2 text-base">
              <Zap className="w-4 h-4" />
              Të dhëna bazë
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="name">Emri i karikuesit *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Emri i shfaqur"
                  data-testid="charger-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_power">Fuqia maksimale (kW)</Label>
                <Input
                  id="max_power"
                  type="number"
                  step="0.1"
                  value={formData.max_power_kw}
                  onChange={(e) => handleInputChange('max_power_kw', e.target.value)}
                  placeholder="22"
                  data-testid="charger-power-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_per_kwh">Çmimi per kWh (LEK)</Label>
                <Input
                  id="price_per_kwh"
                  type="number"
                  step="0.1"
                  value={formData.price_per_kwh}
                  onChange={(e) => handleInputChange('price_per_kwh', e.target.value)}
                  placeholder="25"
                  data-testid="charger-price-input"
                />
                <p className="text-xs text-muted-foreground">Lër bosh për çmimin global</p>
              </div>
              <div className="space-y-2">
                <Label>Vendndodhja (Site)</Label>
                <Select
                  value={formData.site_id}
                  onValueChange={(v) => handleInputChange('site_id', v)}
                >
                  <SelectTrigger data-testid="site-select">
                    <SelectValue placeholder="Zgjidh vendndodhjen" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_area">Zona / Area</Label>
                <Input
                  id="site_area"
                  value={formData.site_area}
                  onChange={(e) => handleInputChange('site_area', e.target.value)}
                  placeholder="P.sh. Parking A"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <Label>Publik</Label>
                  <p className="text-xs text-muted-foreground">Shfaqet në hartë</p>
                </div>
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(v) => handleInputChange('is_public', v)}
                  data-testid="public-switch"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address & Location with Google Maps */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit flex items-center gap-2 text-base">
              <MapPin className="w-4 h-4" />
              Adresa dhe Vendndodhja
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <LoadScript 
              googleMapsApiKey={GOOGLE_MAPS_API_KEY} 
              libraries={libraries}
              loadingElement={
                <div className="h-[300px] flex items-center justify-center bg-muted rounded-lg">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              }
            >
              {/* Google Places Autocomplete */}
              <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: 'al' },
                  types: ['geocode', 'establishment']
                }}
              >
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground z-10" />
                  <Input
                    placeholder="Kërko adresën (p.sh. Rruga e Elbasanit, Tiranë)..."
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    className="pl-9"
                    data-testid="address-search-input"
                  />
                </div>
              </Autocomplete>
              
              {/* Google Map */}
              <div className="h-[300px] rounded-lg overflow-hidden border border-border mt-3">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={15}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                    scrollwheel: true,
                    gestureHandling: 'greedy'
                  }}
                >
                  {markerPosition && (
                    <Marker 
                      position={markerPosition}
                      draggable={true}
                      onDragEnd={handleMarkerDrag}
                    />
                  )}
                </GoogleMap>
              </div>
            </LoadScript>
            
            <p className="text-xs text-muted-foreground text-center">
              Kërko adresën, kliko në hartë ose tërhiq markerin për të vendosur vendndodhjen
            </p>
            
            {formData.latitude && formData.longitude && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Adresa</p>
                  <p className="font-medium truncate">{formData.address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Qyteti</p>
                  <p className="font-medium">{formData.city || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Lat</p>
                  <p className="font-medium font-mono text-green-500">{formData.latitude?.toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Lng</p>
                  <p className="font-medium font-mono text-green-500">{formData.longitude?.toFixed(5)}</p>
                </div>
              </div>
            )}
        </CardContent>
      </Card>
      </div>

      {/* Row 2: Photos + Amenities - 50% each */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Photos */}
        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit flex items-center gap-2 text-base">
              <Image className="w-4 h-4" />
              Foto të Karikuesit
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {/* Upload area */}
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                id="photo-upload"
              />
              
              {photoPreview ? (
                <div className="space-y-3">
                  <img 
                    src={photoPreview} 
                    alt="Preview" 
                    className="max-h-24 mx-auto rounded-lg"
                  />
                  <div className="flex justify-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPhotoPreview(null);
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Anulo
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleUploadPhoto(false)}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      Ngarko
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleUploadPhoto(true)}
                      disabled={uploading}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Kryesore
                    </Button>
                  </div>
                </div>
              ) : (
                <label htmlFor="photo-upload" className="cursor-pointer block">
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-1" />
                  <p className="text-sm text-muted-foreground">Kliko për të ngarkuar foto</p>
                  <p className="text-xs text-muted-foreground">JPG, PNG, WebP (max 5MB)</p>
                </label>
              )}
            </div>
            
            {/* Existing photos */}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo) => (
                  <div 
                    key={photo.id} 
                    className="relative group rounded-lg overflow-hidden border border-border"
                  >
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                      alt="Charger photo"
                      className="w-full h-16 object-cover"
                    />
                    {photo.is_cover && (
                      <div className="absolute top-1 left-1 bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                        <Star className="w-2.5 h-2.5" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      {!photo.is_cover && (
                        <Button
                          size="icon"
                          variant="secondary"
                          className="h-6 w-6"
                          onClick={() => handleSetCover(photo.id)}
                        >
                          <Star className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-6 w-6"
                        onClick={() => handleDeletePhoto(photo.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Amenities / Shërbime */}
        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit flex items-center gap-2 text-base">
              <Info className="w-4 h-4" />
              Shërbime / Facilitete
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {AMENITIES_LIST.map((amenity) => {
                const isSelected = formData.amenities?.includes(amenity.id);
                return (
                  <div
                    key={amenity.id}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                      isSelected 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        amenities: isSelected
                          ? prev.amenities.filter(a => a !== amenity.id)
                          : [...(prev.amenities || []), amenity.id]
                      }));
                    }}
                    data-testid={`amenity-${amenity.id}`}
                  >
                    <span className="text-base">{amenity.icon}</span>
                    <span className={`text-xs ${isSelected ? 'font-medium' : ''}`}>
                      {amenity.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChargerEdit;
